import { useState, useCallback, type ClipboardEvent } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { useForm } from 'react-hook-form';

import {
  useAgregarComentario,
  useAgregarComentarioConArchivos,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import FileDropzoneField from '@/shared/components/ui/FileDropzoneField';
import {
  isAcceptedFile,
  MAX_FILE_BYTES,
  formatBytes,
} from '@/shared/components/ui/FileDropzoneField/FileDropzoneField';

type FormComentarioProps = {
  ticketId: string;
};

type FormData = {
  comentario: string;
};

export default function FormComentario({ ticketId }: FormComentarioProps) {
  const [archivos, setArchivos] = useState<File[]>([]);
  const [errorForm, setErrorForm] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { comentario: '' } });

  const agregarComentario = useAgregarComentario();
  const agregarConArchivos = useAgregarComentarioConArchivos();

  const onSubmit = (data: FormData) => {
    const texto = data.comentario?.trim() ?? '';
    const tieneArchivos = archivos.length > 0;

    if (!texto && !tieneArchivos) {
      setErrorForm('Escriba un comentario o adjunte al menos un archivo');
      return;
    }

    if (tieneArchivos) {
      agregarConArchivos.mutate(
        { id: ticketId, comentario: texto, archivos },
        {
          onSuccess: () => {
            reset();
            setArchivos([]);
            setErrorForm(null);
          },
        },
      );
    } else {
      agregarComentario.mutate(
        { id: ticketId, data: { comentario: texto } },
        {
          onSuccess: () => {
            reset();
            setErrorForm(null);
          },
        },
      );
    }
  };

  const enviando = agregarComentario.isPending || agregarConArchivos.isPending;
  const comentarioTexto = watch('comentario');
  const puedeEnviar = (comentarioTexto?.trim().length ?? 0) > 0 || archivos.length > 0;

  // Normaliza un archivo pegado: a las imagenes del portapapeles (sin nombre o
  // con el generico "image.png") les asigna un nombre unico para no colisionar
  // ni quedar vacias. Los archivos copiados con nombre real se conservan igual.
  const normalizarPegado = (f: File, idx: number): File => {
    const generico = !f.name || f.name.toLowerCase() === 'image.png';
    if (!generico) return f;
    const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
    return new File([f], `captura-${Date.now()}-${idx}.${ext}`, { type: f.type });
  };

  // Pegar (Ctrl+V) imagenes/archivos del portapapeles como adjuntos. Este form
  // solo se monta para tickets ABIERTOS (no se renderiza si el ticket esta
  // cerrado), por lo que el pegado queda inactivo en tickets cerrados.
  // Si el portapapeles trae SOLO texto, no interferimos: se pega normal.
  const handlePaste = useCallback((e: ClipboardEvent<HTMLFormElement>) => {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;

    const pegados: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const f = items[i].getAsFile();
        if (f) pegados.push(f);
      }
    }
    if (pegados.length === 0) return; // solo texto -> pegado normal

    e.preventDefault(); // hay archivos -> evitar que el textarea pegue basura

    const validos: File[] = [];
    const errores: string[] = [];
    pegados.forEach((raw, idx) => {
      const f = normalizarPegado(raw, idx);
      if (!isAcceptedFile(f)) {
        errores.push(`Tipo no permitido: ${f.name || f.type || 'archivo'}`);
      } else if (f.size > MAX_FILE_BYTES) {
        errores.push(`"${f.name}" excede ${formatBytes(MAX_FILE_BYTES)}`);
      } else {
        validos.push(f);
      }
    });

    if (validos.length > 0) setArchivos((prev) => [...prev, ...validos]);
    setErrorForm(errores.length > 0 ? errores.join(' · ') : null);
  }, []);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} onPaste={handlePaste} sx={{ mt: 2 }}>
      <Stack spacing={1.5}>
        <TextField
          {...register('comentario')}
          label="Agregar comentario"
          placeholder="Escriba un comentario (opcional si adjunta archivos)"
          multiline
          minRows={2}
          fullWidth
          size="small"
          error={!!errors.comentario}
          helperText={errors.comentario?.message}
        />

        <Stack direction="row" spacing={0.5} alignItems="center">
          <ContentPasteIcon fontSize="inherit" color="action" />
          <Typography variant="caption" color="text.secondary">
            Tip: también puedes pegar (Ctrl+V) una imagen o archivo para adjuntarlo.
          </Typography>
        </Stack>

        <FileDropzoneField
          value={archivos}
          onChange={setArchivos}
          error={errorForm}
        />

        {errorForm && !archivos.length && (
          <Typography variant="caption" color="error">
            {errorForm}
          </Typography>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            size="small"
            startIcon={archivos.length > 0 ? <AttachFileIcon /> : undefined}
            disabled={!puedeEnviar || enviando}
          >
            {archivos.length > 0
              ? `Agregar Comentario (${archivos.length})`
              : 'Agregar Comentario'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
